/**
 * onboardingPrimitives.jsx — v213.7 (C-A-OB-FIX)
 *
 * Shared visual primitives for the 7-screen onboarding flow.
 *
 * v213.7 changes:
 *  - All body text ≥14pt (was 11–13.5px). Labels 13px minimum.
 *  - SkipLink → SecondaryButton: real outlined button, 48px tall,
 *    matches PrimaryButton geometry. No more text-link skips.
 *  - OnboardingShell: dropped minHeight: 100vh → content sizes itself,
 *    no more dead white space below CTAs.
 *  - useScrollToTop hook: every screen mount resets scroll to top.
 *  - "from scan" prefilled pill bumped to 11px.
 */
import { Children, isValidElement, useEffect, useLayoutEffect, useState } from 'react'
import { ChevronLeft, X, Check, Plus, Sparkles } from 'lucide-react'
import orellaOIcon from '../../assets/logos/orella_o_mark.png'
import { useI18n } from '../../i18n'
import BottomActionBar from '../../components/BottomActionBar'

// Brand tokens — must match design system
export const NAVY = '#15354F'
export const BLUE = '#46A8F9'
export const LIGHT_BLUE = '#A7D9FC'
export const ICE_BLUE = '#E9F8FF'
export const GREEN = '#66D170'
export const PASTEL_GREEN = '#D5EFD3'
export const TEXT_MUTED = '#5B6E80'
export const TEXT_HINT = '#7B8A99'
export const BORDER_LIGHT = '#E5EDF3'

// s95 sign-up rebuild: sign-up is THREE required steps (Security, About you, Agreements).
// The health-profile screens are an optional second run of FIVE (Insurance, Records,
// Allergies, Medications, More), offered after sign-up — each counts its own run.
export const TOTAL_STEPS = 3
export const PROFILE_STEPS = 5

/**
 * useScrollToTop — call inside every onboarding screen so transitions
 * always land at the top. Belt-and-suspenders against the previous
 * screen's scroll position carrying over.
 */
export function useScrollToTop(stepKey) {
  // v240.11.0: useLayoutEffect (was useEffect) so the reset lands BEFORE the
  // browser paints the incoming screen. Onboarding is a single route stepped via
  // state, so App.jsx's route-change scroll reset never fires between steps —
  // this hook is the only reset. With useEffect (post-paint) a tall step painted
  // one frame at the previous .scroll-wrapper offset (looked like it "didn't open
  // at the top"), then jumped. Pre-paint reset removes that.
  useLayoutEffect(() => {
    // Standard window scroll
    try { window.scrollTo({ top: 0, behavior: 'auto' }) } catch {}
    // The app uses .scroll-wrapper as the actual scroll container
    try {
      const wrapper = document.querySelector('.scroll-wrapper')
      if (wrapper) wrapper.scrollTop = 0
    } catch {}
    // Belt-and-suspenders for any UA that scrolls the document element instead
    try {
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0
    } catch {}
  }, [stepKey])
}

/**
 * ProgressDots — segmented progress track + "N / total" count.
 * Past = navy, current = blue, future = light track. Fills its container width.
 */
export function ProgressDots({ current, total = TOTAL_STEPS }) {
  return (
    <div className="flex items-center w-full" style={{ gap: 10 }}>
      <div className="flex flex-1" style={{ gap: 3 }}>
        {Array.from({ length: total }).map((_, i) => {
          const bg = i < current ? NAVY : i === current ? BLUE : BORDER_LIGHT
          return (
            <div
              key={i}
              className="transition-all duration-300"
              style={{ flex: 1, height: 5, borderRadius: 3, background: bg }}
            />
          )
        })}
      </div>
      <span
        className="font-semibold"
        style={{ fontSize: 12, color: TEXT_MUTED, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
      >
        {current + 1} / {total}
      </span>
    </div>
  )
}

/**
 * ScreenHeader — back button row, then a full-width segmented progress track.
 */
export function ScreenHeader({ onBack, step, showBack = true, total = TOTAL_STEPS, onExit, showProgress = true }) {
  return (
    <div className="px-5 pt-3 pb-2 bg-white">
      <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
        {showBack ? (
          <button
            onClick={onBack}
            className="rounded-full flex items-center justify-center"
            style={{ width: 36, height: 36, background: '#fff', border: `1px solid ${BORDER_LIGHT}`, padding: 0, cursor: 'pointer' }}
            aria-label="Go back"
          >
            <ChevronLeft size={18} style={{ color: NAVY }} />
          </button>
        ) : (
          <div style={{ width: 36, height: 36 }} />
        )}
        {onExit && <ExitButton onExit={onExit} />}
      </div>
      {/* PROD-1225: the re-consent screen is not a step of a sequence, so it has no counter. */}
      {showProgress && <ProgressDots current={step} total={total} />}
    </div>
  )
}

/**
 * ExitButton — s95b. Every sign-up screen can be left before it is finished; the flow
 * asks first, then signs out to the Welcome screen (OnboardingFlow owns what "leave" does).
 */
export function ExitButton({ onExit }) {
  const { t } = useI18n()
  return (
    <button
      type="button"
      onClick={onExit}
      className="rounded-full flex items-center justify-center"
      style={{ width: 36, height: 36, background: '#fff', border: `1px solid ${BORDER_LIGHT}`, padding: 0, cursor: 'pointer' }}
      aria-label={t('onboarding.exit.button', 'Exit sign-up')}
    >
      <X size={18} style={{ color: NAVY }} />
    </button>
  )
}

/**
 * ScreenIntro — eyebrow (section name) + optional Required chip + title.
 * Eyebrow carries the section name only; position lives in ProgressDots.
 */
export function ScreenIntro({ eyebrow, title, subtitle, dialogue, required = false }) {
  const { t } = useI18n()
  return (
    <div className="px-5 pt-3 pb-1">
      {eyebrow && (
        <div className="flex items-center" style={{ gap: 10, marginTop: 6, marginBottom: 9 }}>
          <p
            className="font-semibold uppercase"
            style={{ fontSize: 13, letterSpacing: '0.10em', color: BLUE, margin: 0 }}
          >
            {eyebrow}
          </p>
          {required && (
            <span
              className="font-bold uppercase"
              style={{
                fontSize: 10,
                letterSpacing: '0.04em',
                color: NAVY,
                background: ICE_BLUE,
                border: `1px solid ${BORDER_LIGHT}`,
                borderRadius: 999,
                padding: '3px 9px',
                whiteSpace: 'nowrap',
              }}
            >
              {t('onboarding.required', 'Required')}
            </span>
          )}
        </div>
      )}
      <h1
        className="font-medium"
        style={{ fontSize: 26, lineHeight: 1.2, letterSpacing: '-0.014em', color: NAVY, marginBottom: 16 }}
      >
        {title}
      </h1>
      {dialogue ? (
        <OrellaDialogue>{dialogue}</OrellaDialogue>
      ) : (
        subtitle && (
          <p style={{ fontSize: 15, color: TEXT_MUTED, lineHeight: 1.5 }}>
            {subtitle}
          </p>
        )
      )}
    </div>
  )
}

/**
 * OrellaDialogue — canonical Orella speech: the bare navy O glyph (no tile)
 * beside an ice-blue bubble with a light-blue border. Left-aligned, matches
 * the app's home/header dialogue pattern. Carries each screen's step
 * description in Orella's voice.
 */
export function OrellaDialogue({ children }) {
  // Staggered "dialogue" reveal matching the app's home/header chat pattern:
  // the O glyph pops in, the bubble slides up with typing dots, then the dots
  // swap for the message fading in.
  const [showLogo, setShowLogo] = useState(false)
  const [showBubble, setShowBubble] = useState(false)
  const [showDots, setShowDots] = useState(false)
  const [showText, setShowText] = useState(false)

  useEffect(() => {
    const timers = []
    timers.push(setTimeout(() => setShowLogo(true), 100))
    timers.push(setTimeout(() => { setShowBubble(true); setShowDots(true) }, 220))
    timers.push(setTimeout(() => { setShowDots(false); setShowText(true) }, 720))
    return () => timers.forEach(clearTimeout)
  }, [])

  const dot = (delay) => ({
    width: 6, height: 6, borderRadius: '50%', background: LIGHT_BLUE,
    animation: `orellaDlgDot 1.2s ease-in-out infinite ${delay}`,
  })

  return (
    <>
      <style>{`
        @keyframes orellaDlgDot { 0%,80%,100%{opacity:.3;transform:scale(.8)} 40%{opacity:1;transform:scale(1.1)} }
        @keyframes orellaDlgFade { from{opacity:0} to{opacity:1} }
      `}</style>
      <div className="flex items-start" style={{ gap: 11, marginTop: 2, marginBottom: 6 }}>
        <img
          src={orellaOIcon}
          alt="Orella"
          style={{
            width: 40, height: 40, objectFit: 'contain', flexShrink: 0, marginTop: 1,
            opacity: showLogo ? 1 : 0,
            transform: showLogo ? 'scale(1)' : 'scale(0.75)',
            transition: 'opacity 0.3s ease, transform 0.3s cubic-bezier(0.16,1,0.3,1)',
          }}
        />
        <div
          style={{
            flex: 1,
            background: ICE_BLUE,
            border: `1px solid ${LIGHT_BLUE}`,
            borderRadius: '4px 18px 18px 18px',
            padding: '13px 15px',
            color: NAVY,
            fontSize: 15.5,
            fontWeight: 500,
            lineHeight: 1.55,
            minHeight: 22,
            opacity: showBubble ? 1 : 0,
            transform: showBubble ? 'translateY(0) scale(1)' : 'translateY(4px) scale(0.97)',
            transition: 'opacity 0.3s ease, transform 0.3s cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          {showDots && !showText && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 20 }}>
              <span style={dot('0s')} />
              <span style={dot('0.2s')} />
              <span style={dot('0.4s')} />
            </span>
          )}
          {showText && <span style={{ animation: 'orellaDlgFade 0.3s ease' }}>{children}</span>}
        </div>
      </div>
    </>
  )
}

/**
 * FilledBanner — provenance banner shown atop an autofilled section
 * (allergies / medications / history). Confirms the items came from the
 * patient's uploaded record and cues them to review/confirm.
 */
export function FilledBanner({ children }) {
  return (
    <div
      className="flex items-center"
      style={{
        gap: 6,
        width: '100%',
        fontSize: 12,
        fontWeight: 600,
        color: '#0F6E56',
        background: 'rgba(102,209,112,0.12)',
        border: '1px solid rgba(102,209,112,0.4)',
        borderRadius: 10,
        padding: '8px 11px',
        marginBottom: 10,
      }}
    >
      <Sparkles size={14} color="#0F9E6E" />
      <span style={{ flex: 1 }}>{children}</span>
    </div>
  )
}

/**
 * OrellaBubble — translucent ICE_BLUE chat bubble with the Orella mark.
 */
export function OrellaBubble({ children }) {
  return (
    <div className="flex items-start gap-2.5 px-5 pt-3 pb-2">
      <div
        className="flex-shrink-0 rounded-[10px] flex items-center justify-center"
        style={{
          width: 36, height: 36,
          background: ICE_BLUE,
          border: `1.5px solid ${LIGHT_BLUE}`,
        }}
      >
        <img src={orellaOIcon} alt="" style={{ width: 22, height: 22, objectFit: 'contain' }} />
      </div>
      <div
        className="flex-1 rounded-[14px] px-3.5 py-2.5"
        style={{
          background: ICE_BLUE,
          border: `1.5px solid ${LIGHT_BLUE}`,
          color: NAVY,
          fontSize: 14,
          fontWeight: 500,
          lineHeight: 1.4,
        }}
      >
        {children}
      </div>
    </div>
  )
}

/**
 * PrimaryButton — full-width pill CTA with brand breathe pulse.
 */
export function PrimaryButton({ children, onClick, variant = 'navy', disabled = false, ...rest }) {
  const styles = {
    navy:  { bg: NAVY,  color: '#fff',  anim: 'breathe-navy'  },
    blue:  { bg: BLUE,  color: '#fff',  anim: 'breathe-blue'  },
    green: { bg: GREEN, color: NAVY,    anim: 'breathe-green' },
  }[variant]

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full font-medium transition ${disabled ? '' : styles.anim}`}
      style={{
        height: 50,
        borderRadius: 999,
        border: 'none',
        fontSize: 15,
        background: disabled ? '#D8E6F0' : styles.bg,
        color: disabled ? '#9CA3AF' : styles.color,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      {...rest}
    >
      {children}
    </button>
  )
}

/**
 * SecondaryButton — outlined navy ring, 48px tall, real button feel.
 * Replaces the old text-link SkipLink for "No known allergies" / "Skip"
 * patterns where we want a legitimate secondary action.
 */
export function SecondaryButton({ children, onClick, variant = 'navy' }) {
  const palette = {
    navy: { color: NAVY, border: NAVY },
    blue: { color: BLUE, border: BLUE },
    muted: { color: TEXT_MUTED, border: BORDER_LIGHT },
  }[variant]

  return (
    <button
      onClick={onClick}
      className="w-full font-medium transition"
      style={{
        height: 48,
        borderRadius: 999,
        border: `1.5px solid ${palette.border}`,
        background: '#fff',
        color: palette.color,
        fontSize: 14.5,
        marginTop: 10,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  )
}

/**
 * TextField — onboarding-styled input. Min 14pt body.
 */
export function TextField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  required = true,
  prefilled = false,
  hint,
  hintTone = 'neutral',
  inputMode,
  autoComplete,
  maxLength,
  // TEST 3.01 (242.229.0): shown, never edited — the account's own Email / Mobile
  readOnly = false,
  // PROD-696 (P4): optional; the onboarding member ID is checked when the field loses focus
  onBlur,
}) {
  return (
    <div className="mb-3">
      <label
        className="block font-medium mb-1.5"
        style={{ fontSize: 13, color: NAVY }}
      >
        {label}
        {required && <span style={{ color: '#DC2626', marginLeft: 2 }}>*</span>}
        {prefilled && (
          <span
            className="ml-1.5 inline-flex items-center font-medium"
            style={{
              fontSize: 11,
              padding: '2px 7px',
              borderRadius: 999,
              background: ICE_BLUE,
              color: BLUE,
            }}
          >
            from scan
          </span>
        )}
      </label>
      <input
        type={type}
        value={value || ''}
        readOnly={readOnly}
        aria-readonly={readOnly || undefined}
        onChange={(e) => { if (!readOnly) onChange(e.target.value) }}
        placeholder={placeholder}
        inputMode={inputMode}
        autoComplete={autoComplete}
        maxLength={maxLength}
        className="w-full focus:outline-none transition"
        style={{
          height: 46,
          padding: '0 12px',
          borderRadius: 11,
          border: `1.5px solid ${BORDER_LIGHT}`,
          background: readOnly ? '#F8FAFC' : '#fff',
          color: NAVY,
          fontSize: 15,
        }}
        onFocus={(e) => { if (!readOnly) e.target.style.borderColor = BLUE }}
        onBlur={(e) => { e.target.style.borderColor = BORDER_LIGHT; if (onBlur) onBlur(e) }}
      />
      {hint && (
        <p
          style={{
            fontSize: 12.5,
            marginTop: 5,
            color: hintTone === 'warning' ? '#B45309' : TEXT_HINT,
          }}
        >
          {hint}
        </p>
      )}
    </div>
  )
}

/**
 * SegmentedSelect — pill-style options. Min 14pt.
 */
export function SegmentedSelect({ label, options, value, onChange, required = true }) {
  return (
    <div className="mb-3">
      <label className="block font-medium mb-1.5" style={{ fontSize: 13, color: NAVY }}>
        {label}
        {required && <span style={{ color: '#DC2626', marginLeft: 2 }}>*</span>}
      </label>
      <div className="flex gap-1.5">
        {options.map((opt) => {
          const selected = opt.value === value
          return (
            <button
              key={opt.value}
              onClick={() => onChange(opt.value)}
              className="flex-1 font-medium transition"
              style={{
                height: 42,
                borderRadius: 10,
                fontSize: 14,
                background: selected ? ICE_BLUE : '#fff',
                border: `1.5px solid ${selected ? BLUE : BORDER_LIGHT}`,
                color: selected ? BLUE : NAVY,
                cursor: 'pointer',
              }}
            >
              {opt.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/**
 * OnboardingShell — outer container. v213.7: dropped minHeight: 100vh.
 * Content fills its natural height; no white dead space below.
 */
export function OnboardingShell({ children }) {
  // PROD-1194: flex: 1 so the step fills the frame and its Footer bar (margin-top: auto) sits at the
  // bottom of the screen on a short step, not under the last line of content.
  return (
    <div
      className="flex flex-col"
      style={{ background: '#fff', flex: 1 }}
    >
      {children}
    </div>
  )
}

/**
 * ConfirmCard — AI-extracted item card, tap to confirm, X to remove.
 */
export function ConfirmCard({ confirmed, onToggle, onRemove, children }) {
  return (
    <div
      className="rounded-[14px] mb-2 cursor-pointer transition"
      onClick={onToggle}
      style={{
        padding: 13,
        background: confirmed ? PASTEL_GREEN + '40' : '#fff',
        border: `1.5px solid ${confirmed ? GREEN : LIGHT_BLUE}`,
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex-shrink-0 rounded-full flex items-center justify-center"
          style={{
            width: 26, height: 26, marginTop: 2,
            background: confirmed ? GREEN : '#F2F4F7',
          }}
        >
          {confirmed && <Check size={14} color="#fff" strokeWidth={3} />}
        </div>
        <div className="flex-1">{children}</div>
        <button
          onClick={(e) => { e.stopPropagation(); onRemove?.() }}
          className="rounded-full flex items-center justify-center"
          style={{
            width: 26, height: 26,
            background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
          }}
          aria-label="Remove"
        >
          <X size={15} color={TEXT_HINT} />
        </button>
      </div>
    </div>
  )
}

/**
 * AddAnotherButton — dashed "+ Add another" CTA below confirmed cards.
 */
export function AddAnotherButton({ label = 'Add another', onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full inline-flex items-center justify-center gap-2 font-medium transition"
      style={{
        padding: 13,
        borderRadius: 14,
        background: '#fff',
        border: `1.5px dashed ${LIGHT_BLUE}`,
        color: BLUE,
        fontSize: 14,
        marginTop: 6,
        cursor: 'pointer',
      }}
    >
      <Plus size={16} />
      {label}
    </button>
  )
}

/**
 * EmptyState — left-aligned empty state with primary "Add" CTA.
 */
export function EmptyState({ icon: IconComp, title, ctaLabel, onAdd }) {
  return (
    <div className="flex flex-col items-start" style={{ padding: '20px 0 16px' }}>
      <div
        className="rounded-[14px] flex items-center justify-center mb-3.5"
        style={{ width: 60, height: 60, background: ICE_BLUE }}
      >
        <IconComp size={28} color={BLUE} />
      </div>
      <p className="font-medium mb-3.5" style={{ fontSize: 15, color: NAVY }}>{title}</p>
      <button
        onClick={onAdd}
        className="inline-flex items-center gap-2 font-medium"
        style={{
          padding: '10px 20px',
          borderRadius: 999,
          background: BLUE,
          color: '#fff',
          fontSize: 14,
          border: 'none',
          cursor: 'pointer',
        }}
      >
        <Plus size={16} />
        {ctaLabel}
      </button>
    </div>
  )
}

/**
 * ScrollBody — middle scroll area used on form-heavy screens.
 */
export function ScrollBody({ children, className = '', style = {} }) {
  return (
    <div className={`px-5 pb-3 ${className}`} style={{ paddingTop: 14, ...style }}>
      {children}
    </div>
  )
}

/**
 * Footer — bottom region with primary CTA and optional secondary button.
 *
 * ★ PROD-1194: the Footer IS the pinned bar (components/BottomActionBar): every onboarding step's
 *   primary button stays at the bottom of the screen while the step scrolls above it, and the bar
 *   pays the home-indicator inset itself (OnboardingFrame no longer does). With at most ONE
 *   SecondaryButton everything stays in the bar; with more (Insurance), the primary and what
 *   precedes it are pinned and the rest follows the bar and scrolls with the content (a bar of
 *   three outlined buttons would cover half a small screen). No buttons (a sub-step without one):
 *   only the inset is kept, nothing is pinned.
 *   `note`: a line pinned in the bar above the primary (BottomActionBar's note slot), e.g. the
 *   sign-up finish error, which a fixed toast used to lay over the button (PROD-1194 review M2).
 */
export function Footer({ children, note = null }) {
  const items = Children.toArray(children)
  if (!items.length) return <div aria-hidden className="bottom-actions" data-footer-empty />
  const secondaries = items.filter((c) => isValidElement(c) && c.type === SecondaryButton).length
  const cut = secondaries > 1 ? items.findIndex((c) => isValidElement(c) && c.type === PrimaryButton) + 1 : 0
  if (cut <= 0) return <BottomActionBar note={note}>{items}</BottomActionBar>
  return (
    <>
      <BottomActionBar note={note}>{items.slice(0, cut)}</BottomActionBar>
      <div className="px-5 bottom-actions [--ba-min:18px]" data-footer-rest style={{ paddingTop: 2 }}>
        {items.slice(cut)}
      </div>
    </>
  )
}
